import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Upload, FileText, Calendar, CheckCircle2, Building2,
  X, Image as ImageIcon, File as FileIcon, AlertCircle, Plus, Loader2,
  BarChart3,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useNavigation } from '../hooks/useMe';
import { useAuthStore } from '../store/auth';
import {
  uploadInspectionImage, uploadInspectionReportPdf, writeInspectionSidecar,
} from '../hooks/api/uploads';

/**
 * Annotator Upload — writes to the canonical S3 inspection layout:
 *   {client}/{industry}/{project}/{deliverable}/{DDMMYYYY}/
 *     images/{filename}      ← every defect image
 *     reports.json           ← built from the "Defect Images with Details" form
 *     report.pdf             ← the PDF the annotator uploads
 *     analytics.json         ← the analytics file the annotator uploads
 *
 * The Reports + Analytics tabs read from this exact layout, so anything
 * submitted here shows up there immediately for the same client / industry
 * / project / deliverable / date.
 */
export function AnnotatorUpload() {
  const userClients = useAuthStore((s) => s.user?.clients || []);
  const setActiveClientId = useAuthStore((s) => s.setActiveClientId);
  const activeClientId = useAuthStore((s) => s.activeClientId);
  const navigation = useNavigation();

  const [selectedClientId, setSelectedClientId] = useState(activeClientId || '');
  const [selectedIndustryId, setSelectedIndustryId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedDeliverableId, setSelectedDeliverableId] = useState('');
  const [taskDate, setTaskDate] = useState('');
  const [comments, setComments] = useState('');

  const [pdfReport, setPdfReport] = useState(null);
  const [analyticsFile, setAnalyticsFile] = useState(null);
  const [analyticsParseError, setAnalyticsParseError] = useState('');
  // Each defect: { id, image, details: [{key, value}] }
  const [defects, setDefects] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  const industries = navigation?.industries || [];
  const projects = useMemo(() => {
    return industries.find((i) => i.id === selectedIndustryId)?.projects || [];
  }, [industries, selectedIndustryId]);
  const deliverables = useMemo(() => {
    return projects.find((p) => p.id === selectedProjectId)?.deliverables || [];
  }, [projects, selectedProjectId]);

  // Switching the client also pins X-Client-Id (so the navigation tree
  // refetches under the new tenant).
  const onClientChange = (v) => {
    setSelectedClientId(v);
    setActiveClientId(v);
    setSelectedIndustryId(''); setSelectedProjectId(''); setSelectedDeliverableId('');
  };
  const onIndustryChange = (v) => {
    setSelectedIndustryId(v); setSelectedProjectId(''); setSelectedDeliverableId('');
  };
  const onProjectChange = (v) => {
    setSelectedProjectId(v); setSelectedDeliverableId('');
  };

  const handlePdfUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')) {
      setPdfReport(f);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const handleAnalyticsFile = async (e) => {
    const f = e.target.files?.[0];
    setAnalyticsParseError('');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.json') && f.type !== 'application/json') {
      toast.error('Please upload a .json file');
      return;
    }
    try {
      const text = await f.text();
      JSON.parse(text); // sanity-check before submission
      setAnalyticsFile(f);
    } catch (err) {
      setAnalyticsParseError(err.message || 'Invalid JSON');
      toast.error('Could not parse analytics.json — check the file content');
    }
  };

  // Defect rows
  const addDefect = () =>
    setDefects((prev) => [
      ...prev, { id: String(Date.now()) + Math.random(), image: null, details: [] },
    ]);
  const removeDefect = (id) => setDefects((prev) => prev.filter((d) => d.id !== id));
  const setDefectImage = (id, file) =>
    setDefects((prev) => prev.map((d) => d.id === id ? { ...d, image: file } : d));
  const addDetailField = (id) =>
    setDefects((prev) => prev.map((d) =>
      d.id === id ? { ...d, details: [...d.details, { key: '', value: '' }] } : d,
    ));
  const updateDetailField = (id, idx, field, value) =>
    setDefects((prev) => prev.map((d) => {
      if (d.id !== id) return d;
      const next = [...d.details];
      next[idx] = { ...next[idx], [field]: value };
      return { ...d, details: next };
    }));
  const removeDetailField = (id, idx) =>
    setDefects((prev) => prev.map((d) =>
      d.id === id ? { ...d, details: d.details.filter((_, i) => i !== idx) } : d,
    ));

  const resetForm = () => {
    setSelectedIndustryId(''); setSelectedProjectId(''); setSelectedDeliverableId('');
    setTaskDate(''); setComments('');
    setPdfReport(null); setAnalyticsFile(null); setAnalyticsParseError('');
    setDefects([]);
  };

  const requirementsMet = (
    selectedClientId && selectedIndustryId && selectedProjectId &&
    selectedDeliverableId && taskDate
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!requirementsMet) {
      toast.error('Please pick Client, Industry, Project, Deliverable and Task Date');
      return;
    }
    if (defects.length === 0 && !pdfReport && !analyticsFile) {
      toast.error('Add at least one defect image, the PDF report, or analytics.json');
      return;
    }

    setSubmitting(true);
    const ctx = {
      industry_id:    selectedIndustryId,
      project_id:     selectedProjectId,
      deliverable_id: selectedDeliverableId,
      task_date:      taskDate, // backend accepts ISO YYYY-MM-DD
    };

    let imageErrors = 0;
    let imageOk = 0;

    // 1) Build reports.json entries up-front from form state. Each defect
    //    that has a file picked automatically gets image_path: images/<name>
    //    pointing at where the matching upload will land in S3 — even if
    //    the upload itself fails later, the JSON still references the
    //    canonical path so a retry can backfill the file.
    const reportEntries = defects.map((d) => {
      const detailObj = d.details.reduce((acc, dt) => {
        if (dt.key && dt.value) acc[dt.key] = dt.value;
        return acc;
      }, {});
      if (d.image) {
        detailObj.image_path = `images/${d.image.name}`;
      }
      return detailObj;
    }).filter((entry) => Object.keys(entry).length > 0);

    // 2) Upload each defect image to {prefix}/images/{filename}. Failures
    //    are surfaced via toasts but don't change the JSON we'll write.
    for (const d of defects) {
      if (!d.image) continue;
      try {
        await uploadInspectionImage(d.image, ctx);
        imageOk++;
      } catch (err) {
        imageErrors++;
        toast.error(`${d.image.name}: ${err?.message || 'upload failed'}`);
      }
    }

    // 2) Build + write reports.json (only if there are defect entries)
    if (reportEntries.length > 0) {
      const reportsPayload = {
        InspectionDate: taskDate,
        Comments: comments || undefined,
        EncroachmentAreas: reportEntries,
      };
      try {
        await writeInspectionSidecar('reports_json', reportsPayload, ctx);
      } catch (err) {
        toast.error(`reports.json write failed: ${err?.message || 'unknown error'}`);
      }
    }

    // 3) Upload PDF report
    if (pdfReport) {
      try {
        await uploadInspectionReportPdf(pdfReport, ctx);
      } catch (err) {
        toast.error(`report.pdf upload failed: ${err?.message || 'unknown error'}`);
      }
    }

    // 4) Write analytics.json from the chosen file
    if (analyticsFile) {
      try {
        const text = await analyticsFile.text();
        const parsed = JSON.parse(text);
        await writeInspectionSidecar('analytics_json', parsed, ctx);
      } catch (err) {
        toast.error(`analytics.json write failed: ${err?.message || 'unknown error'}`);
      }
    }

    setSubmitting(false);
    if (imageErrors === 0) {
      toast.success(`Inspection uploaded — ${imageOk} image(s), reports.json, ${pdfReport ? 'report.pdf, ' : ''}${analyticsFile ? 'analytics.json' : ''} written to S3`);
      resetForm();
    } else {
      toast.error(`${imageErrors} of ${imageOk + imageErrors} image(s) failed`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl">Annotator Data Upload</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Files go to <code>s3://&lt;bucket&gt;/&#123;client&#125;/&#123;industry&#125;/&#123;project&#125;/&#123;deliverable&#125;/&#123;DDMMYYYY&#125;/</code>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Inspection scope */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-500" /> Inspection Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field label="Client" required>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                  <Select value={selectedClientId} onValueChange={onClientChange}>
                    <SelectTrigger className="pl-10"><SelectValue placeholder="Select Client" /></SelectTrigger>
                    <SelectContent>
                      {userClients.length === 0 && <SelectItem value="__none" disabled>No clients available</SelectItem>}
                      {userClients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </Field>

              <Field label="Industry" required>
                <Select value={selectedIndustryId} onValueChange={onIndustryChange} disabled={!selectedClientId}>
                  <SelectTrigger><SelectValue placeholder={selectedClientId ? 'Select Industry' : 'Pick a client first'} /></SelectTrigger>
                  <SelectContent>
                    {industries.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Project" required>
                <Select value={selectedProjectId} onValueChange={onProjectChange} disabled={!selectedIndustryId}>
                  <SelectTrigger><SelectValue placeholder={selectedIndustryId ? 'Select Project' : 'Pick an industry first'} /></SelectTrigger>
                  <SelectContent>
                    {projects.length === 0 && (
                      <SelectItem value="__none" disabled>No projects — onboard them first</SelectItem>
                    )}
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Deliverable" required>
                <Select value={selectedDeliverableId} onValueChange={setSelectedDeliverableId} disabled={!selectedProjectId}>
                  <SelectTrigger><SelectValue placeholder={selectedProjectId ? 'Select Deliverable' : 'Pick a project first'} /></SelectTrigger>
                  <SelectContent>
                    {deliverables.length === 0 && (
                      <SelectItem value="__none" disabled>No deliverables — onboard them first</SelectItem>
                    )}
                    {deliverables.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Task Date (becomes the S3 folder name)" required>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="date" className="pl-10" value={taskDate} onChange={(e) => setTaskDate(e.target.value)} />
                </div>
              </Field>
            </div>
          </CardContent>
        </Card>

        {/* PDF Report */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileIcon className="w-5 h-5 text-red-500" /> PDF Report → <code>report.pdf</code>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FileDropzone
              accept=".pdf"
              file={pdfReport}
              onFile={(e) => handlePdfUpload(e)}
              label="Upload PDF Report"
              hint="Click to select a .pdf file"
              icon={FileIcon}
              onClear={() => setPdfReport(null)}
            />
          </CardContent>
        </Card>

        {/* Defect images / videos with details → images/ + reports.json */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Defect Media with Details → <code>images/</code> + <code>reports.json</code>
              </span>
              <Button type="button" size="sm" onClick={addDefect} className="bg-blue-500 hover:bg-blue-600">
                <Plus className="w-4 h-4 mr-2" /> Add Defect
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {defects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No defects added. Click "Add Defect" to start.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {defects.map((defect, index) => (
                  <div key={defect.id} className="p-4 border border-muted-foreground/20 rounded-lg space-y-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Defect #{index + 1}</h4>
                      <Button type="button" variant="ghost" size="sm"
                        onClick={() => removeDefect(defect.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>Defect Media <span className="text-red-500">*</span></Label>
                      <FileDropzone
                        accept="image/*,video/*"
                        file={defect.image}
                        onFile={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          // Accept images and videos. Anything else is rejected
                          // before it goes into form state.
                          const ok = f.type.startsWith('image/') || f.type.startsWith('video/');
                          if (!ok) {
                            toast.error(`${f.name}: only image or video files are allowed`);
                            return;
                          }
                          setDefectImage(defect.id, f);
                        }}
                        label="Upload defect image or video"
                        hint="PNG / JPEG / MP4 / MOV / WebM"
                        icon={ImageIcon}
                        onClear={() => setDefectImage(defect.id, null)}
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Defect Details (Key-Value Pairs) — written into reports.json</Label>
                        <Button type="button" size="sm" variant="outline"
                                onClick={() => addDetailField(defect.id)} className="text-xs">
                          <Plus className="w-3 h-3 mr-1" /> Add Field
                        </Button>
                      </div>

                      {defect.details.length === 0 ? (
                        <div className="text-center py-4 text-sm text-muted-foreground border border-dashed border-muted-foreground/25 rounded-lg">
                          No details added yet.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {defect.details.map((detail, di) => (
                            <div key={di} className="flex items-center gap-2">
                              <Input placeholder="Key (e.g. EncroachmentID, EncroachedArea)" value={detail.key}
                                     onChange={(e) => updateDetailField(defect.id, di, 'key', e.target.value)}
                                     className="flex-1" />
                              <Input placeholder="Value (e.g. R 001, 1429.58 sq mtrs)" value={detail.value}
                                     onChange={(e) => updateDetailField(defect.id, di, 'value', e.target.value)}
                                     className="flex-1" />
                              <Button type="button" variant="ghost" size="sm"
                                onClick={() => removeDetailField(defect.id, di)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* analytics.json upload */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              Analytics → <code>analytics.json</code>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FileDropzone
              accept=".json,application/json"
              file={analyticsFile}
              onFile={handleAnalyticsFile}
              label="Upload analytics.json"
              hint="Click to select a .json file"
              icon={BarChart3}
              onClear={() => { setAnalyticsFile(null); setAnalyticsParseError(''); }}
            />
            {analyticsParseError && (
              <p className="text-xs text-red-500 mt-2">JSON parse error: {analyticsParseError}</p>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="border-0 bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" /> Additional Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Any additional notes — saved into reports.json under `Comments`."
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="resize-none"
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end space-x-4">
          <Button type="button" variant="outline" onClick={resetForm} disabled={submitting}>
            Clear Form
          </Button>
          <Button type="submit" className="bg-primary hover:bg-primary/90"
                  disabled={submitting || !requirementsMet}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {submitting ? 'Uploading…' : 'Submit to S3'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </Label>
      {children}
    </div>
  );
}

function FileDropzone({ accept, file, onFile, label, hint, icon: Icon, onClear }) {
  const id = `dropzone-${React.useId()}`;
  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-muted-foreground/50 transition-colors">
        <input type="file" onChange={onFile} className="hidden" id={id} accept={accept} />
        <label htmlFor={id} className="cursor-pointer">
          <Icon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-foreground mb-1">{label}</p>
          <p className="text-sm text-muted-foreground">{hint}</p>
        </label>
      </div>
      {file && (
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <div>
              <p className="text-sm font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClear}
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
